import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../../../components/AppIcon';

const InteractiveViolationTimeline = ({ violation, remediationActions = [] }) => {
  const [expandedStep, setExpandedStep] = useState(null);

  const getTimelineSteps = () => {
    const steps = [
      {
        id: 'detected',
        title: 'Violation Detected',
        timestamp: violation?.identifiedAt,
        status: 'completed',
        icon: 'AlertTriangle',
        color: 'var(--color-error)',
        description: `${violation?.violationType?.replace(/_/g, ' ')} identified affecting ${violation?.affectedCount} employees`,
        details: {
          severity: violation?.severity,
          estimatedCost: violation?.estimatedCost,
          regulationReference: violation?.regulationReference
        }
      },
      {
        id: 'notified',
        title: 'Alert Sent',
        timestamp: violation?.identifiedAt,
        status: 'completed',
        icon: 'Bell',
        color: 'var(--color-warning)',
        description: 'Automated notifications sent to admin and affected clients',
        details: {
          adminNotified: true,
          clientNotified: true,
          notificationMethod: 'Email + In-App'
        }
      },
      {
        id: 'analysis',
        title: 'AI Analysis',
        timestamp: violation?.identifiedAt,
        status: violation?.violationStatus === 'identified' ? 'in_progress' : 'completed',
        icon: 'Brain',
        color: 'var(--color-primary)',
        description: 'AI-powered remediation suggestions generated',
        details: {
          aiModel: 'GPT-5',
          analysisType: 'Compliance Remediation',
          confidence: '95%'
        }
      },
      {
        id: 'remediation',
        title: 'Remediation Started',
        timestamp: remediationActions?.[0]?.startedAt,
        status: violation?.violationStatus === 'in_progress' ? 'in_progress' : 
                violation?.violationStatus === 'completed' ? 'completed' : 'pending',
        icon: 'Settings',
        color: 'var(--color-primary)',
        description: 'Automated correction process initiated',
        details: {
          actionType: remediationActions?.[0]?.actionType,
          backpayAmount: remediationActions?.[0]?.backpayAmount,
          affectedEmployees: remediationActions?.[0]?.affectedCount
        }
      },
      {
        id: 'verification',
        title: 'Compliance Verified',
        timestamp: violation?.resolvedAt,
        status: violation?.violationStatus === 'completed' ? 'completed' : 'pending',
        icon: 'CheckCircle2',
        color: 'var(--color-success)',
        description: 'Violation resolved and compliance restored',
        details: {
          verificationMethod: 'Automated System Check',
          complianceScore: '100%',
          backpayProcessed: true
        }
      }
    ];

    return steps;
  };

  const timelineSteps = getTimelineSteps();

  const getStatusColor = (status) => {
    if (status === 'completed') return 'bg-success border-success';
    if (status === 'in_progress') return 'bg-primary border-primary animate-pulse';
    return 'bg-muted border-border';
  };

  const getStatusIcon = (status, icon) => {
    if (status === 'completed') return 'CheckCircle2';
    if (status === 'in_progress') return 'Loader2';
    return icon;
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Pending';
    const date = new Date(timestamp);
    return date?.toLocaleString('en-PG', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      <div className="p-4 md:p-6 border-b border-border bg-gradient-to-r from-primary/5 to-transparent">
        <div className="flex items-center gap-2 mb-2">
          <Icon name="GitBranch" size={20} color="var(--color-primary)" />
          <h3 className="font-heading font-semibold text-base md:text-lg text-foreground">
            Interactive Violation Timeline
          </h3>
        </div>
        <p className="text-xs md:text-sm text-muted-foreground">
          Track remediation progress from detection to resolution
        </p>
      </div>
      <div className="p-4 md:p-6">
        <div className="relative">
          {/* Vertical Timeline Line */}
          <div className="absolute left-5 top-0 bottom-0 w-0.5 bg-border" />

          <div className="space-y-6">
            {timelineSteps?.map((step, index) => (
              <motion.div
                key={step?.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
                className="relative"
              >
                {/* Timeline Node */}
                <div className="flex items-start gap-4">
                  <div className={`relative z-10 w-10 h-10 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                    getStatusColor(step?.status)
                  }`}>
                    <Icon 
                      name={getStatusIcon(step?.status, step?.icon)} 
                      size={18} 
                      color={step?.status === 'pending' ? 'var(--color-muted-foreground)' : 'white'}
                      className={step?.status === 'in_progress' ? 'animate-spin' : ''}
                    />
                  </div>

                  {/* Timeline Content */}
                  <div className="flex-1 pb-6">
                    <div className="bg-card border border-border rounded-lg p-4 hover:shadow-md transition-shadow">
                      <div className="flex items-start justify-between gap-3 mb-2">
                        <div className="flex-1">
                          <h4 className="font-heading font-semibold text-sm text-foreground mb-1">
                            {step?.title}
                          </h4>
                          <p className="text-xs text-muted-foreground">
                            {step?.description}
                          </p>
                        </div>
                        <div className="flex flex-col items-end gap-1">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                            step?.status === 'completed' ? 'bg-success/10 text-success' :
                            step?.status === 'in_progress'? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'
                          }`}>
                            {step?.status?.replace('_', ' ')?.toUpperCase()}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {formatTimestamp(step?.timestamp)}
                          </span>
                        </div>
                      </div>

                      {/* Expandable Details */}
                      {step?.details && (
                        <div className="mt-3">
                          <button
                            onClick={() => setExpandedStep(expandedStep === step?.id ? null : step?.id)}
                            className="flex items-center gap-2 text-xs text-primary hover:text-primary/80 transition-colors"
                          >
                            <Icon 
                              name={expandedStep === step?.id ? 'ChevronUp' : 'ChevronDown'} 
                              size={14} 
                            />
                            {expandedStep === step?.id ? 'Hide' : 'View'} Details
                          </button>

                          <AnimatePresence>
                            {expandedStep === step?.id && (
                              <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: 0.2 }}
                                className="overflow-hidden"
                              >
                                <div className="mt-3 p-3 bg-muted/30 rounded-lg space-y-2">
                                  {Object.entries(step?.details)?.map(([key, value]) => (
                                    <div key={key} className="flex items-center justify-between text-xs">
                                      <span className="text-muted-foreground capitalize">
                                        {key?.replace(/([A-Z])/g, ' $1')?.trim()}:
                                      </span>
                                      <span className="font-medium text-foreground">
                                        {typeof value === 'boolean' ? (value ? 'Yes' : 'No') : 
                                         typeof value === 'number' ? `K${value?.toFixed(2)}` : value}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Progress Summary */}
        <div className="mt-6 p-4 bg-gradient-to-r from-primary/5 to-transparent rounded-lg border border-primary/20">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Icon name="TrendingUp" size={16} color="var(--color-primary)" />
              <span className="text-sm font-medium text-foreground">Overall Progress</span>
            </div>
            <span className="text-sm font-bold text-primary">
              {Math.round((timelineSteps?.filter(s => s?.status === 'completed')?.length / timelineSteps?.length) * 100)}%
            </span>
          </div>
          <div className="mt-2 h-2 bg-muted rounded-full overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ 
                width: `${(timelineSteps?.filter(s => s?.status === 'completed')?.length / timelineSteps?.length) * 100}%` 
              }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="h-full bg-gradient-to-r from-primary to-success"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default InteractiveViolationTimeline;